import { useState, useEffect } from 'react';
import { exemplosEventos, dicasSeguranca } from '../../../mocks/worksheet-linha-tempo';

interface EventoTimeline {
  emocao: string;
  evento: string;
  decisaoLimitante: string;
  aprendizado: string;
  novoSignificado: string;
  futuroDesejado: string;
  intensidadeAntes: number;
  intensidadeDepois: number;
}

export default function PraticaTimeline() {
  const [eventoAtual, setEventoAtual] = useState(0);
  const [eventos, setEventos] = useState<EventoTimeline[]>([
    {
      emocao: '',
      evento: '',
      decisaoLimitante: '',
      aprendizado: '',
      novoSignificado: '',
      futuroDesejado: '',
      intensidadeAntes: 5,
      intensidadeDepois: 5
    }
  ]);
  const [dicaAleatoria] = useState(() => dicasSeguranca[Math.floor(Math.random() * dicasSeguranca.length)]);

  useEffect(() => {
    const saved = localStorage.getItem('timeline-eventos');
    if (saved) {
      try {
        setEventos(JSON.parse(saved));
      } catch (e) {
        console.error('Erro ao carregar dados salvos');
      }
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      localStorage.setItem('timeline-eventos', JSON.stringify(eventos));
    }, 30000);
    return () => clearInterval(interval);
  }, [eventos]);

  const calcularProgresso = (evento: EventoTimeline) => {
    let progresso = 0;
    if (evento.emocao.trim()) progresso += 15;
    if (evento.evento.trim().length > 20) progresso += 15;
    if (evento.decisaoLimitante.trim()) progresso += 15;
    if (evento.aprendizado.trim()) progresso += 20;
    if (evento.novoSignificado.trim()) progresso += 20;
    if (evento.futuroDesejado.trim().length > 20) progresso += 15;
    return progresso;
  };

  const atualizarEvento = (campo: keyof EventoTimeline, valor: string | number) => {
    const novosEventos = [...eventos];
    novosEventos[eventoAtual] = {
      ...novosEventos[eventoAtual],
      [campo]: valor
    };
    setEventos(novosEventos);
  };

  const adicionarEvento = () => {
    if (eventos.length < 5) {
      setEventos([...eventos, {
        emocao: '',
        evento: '',
        decisaoLimitante: '',
        aprendizado: '',
        novoSignificado: '',
        futuroDesejado: '',
        intensidadeAntes: 5,
        intensidadeDepois: 5
      }]);
      setEventoAtual(eventos.length);
    }
  };

  const removerEvento = (index: number) => {
    if (eventos.length > 1) {
      const novosEventos = eventos.filter((_, i) => i !== index);
      setEventos(novosEventos);
      if (eventoAtual >= novosEventos.length) {
        setEventoAtual(novosEventos.length - 1);
      }
    }
  };

  const carregarExemplo = () => {
    const exemplo = exemplosEventos[Math.floor(Math.random() * exemplosEventos.length)];
    const novosEventos = [...eventos];
    novosEventos[eventoAtual] = {
      ...exemplo,
      intensidadeAntes: 8,
      intensidadeDepois: 2
    };
    setEventos(novosEventos);
  };

  const eventoAtualData = eventos[eventoAtual];
  const progresso = calcularProgresso(eventoAtualData);

  return (
    <section id="pratica-timeline" className="py-20 bg-gradient-to-br from-gray-50 to-violet-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Prática Interativa
          </h2>
          <p className="text-xl text-gray-600">
            Trabalhe com até 5 eventos diferentes da sua linha do tempo
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              {eventos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setEventoAtual(index)}
                  className={`px-4 py-2 rounded-xl font-semibold transition-all whitespace-nowrap ${
                    eventoAtual === index
                      ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Evento {index + 1}
                </button>
              ))}
              {eventos.length < 5 && (
                <button
                  onClick={adicionarEvento}
                  className="px-4 py-2 rounded-xl font-semibold bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors whitespace-nowrap"
                >
                  + Adicionar
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={carregarExemplo}
                className="px-4 py-2 rounded-xl font-semibold bg-fuchsia-100 text-fuchsia-600 hover:bg-fuchsia-200 transition-colors whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-lightbulb-line"></i>
                Exemplo
              </button>
              {eventos.length > 1 && (
                <button
                  onClick={() => removerEvento(eventoAtual)}
                  className="px-4 py-2 rounded-xl font-semibold bg-red-100 text-red-600 hover:bg-red-200 transition-colors whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              )}
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">Progresso do Evento {eventoAtual + 1}</span>
              <span className="text-sm font-bold text-violet-600">{progresso}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-violet-600 to-fuchsia-600 rounded-full transition-all duration-500"
                style={{ width: `${progresso}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-500 p-4 rounded-r-xl mb-8">
            <div className="flex items-start gap-3">
              <i className="ri-lightbulb-flash-line text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
              <p className="text-sm text-gray-700">
                <strong>Dica de Segurança:</strong> {dicaAleatoria}
              </p>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                <span className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-sm">1</span>
                  Qual emoção negativa você deseja liberar?
                </span>
              </label>
              <select
                value={eventoAtualData.emocao}
                onChange={(e) => atualizarEvento('emocao', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all text-gray-900"
              >
                <option value="">Selecione uma emoção...</option>
                <option value="Raiva">Raiva</option>
                <option value="Tristeza">Tristeza</option>
                <option value="Medo">Medo</option>
                <option value="Culpa">Culpa</option>
                <option value="Vergonha">Vergonha</option>
                <option value="Mágoa">Mágoa</option>
                <option value="Ressentimento">Ressentimento</option>
                <option value="Ansiedade">Ansiedade</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                <span className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-gradient-to-br from-purple-500 to-fuchsia-600 rounded-lg flex items-center justify-center text-white text-sm">2</span>
                  Intensidade Emocional ANTES (0-10)
                </span>
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={eventoAtualData.intensidadeAntes}
                  onChange={(e) => atualizarEvento('intensidadeAntes', parseInt(e.target.value))}
                  className="flex-1 h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, rgb(139, 92, 246) 0%, rgb(139, 92, 246) ${eventoAtualData.intensidadeAntes * 10}%, rgb(229, 231, 235) ${eventoAtualData.intensidadeAntes * 10}%, rgb(229, 231, 235) 100%)`
                  }}
                />
                <span className="text-2xl font-bold text-violet-600 w-12 text-center">
                  {eventoAtualData.intensidadeAntes}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                <span className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-gradient-to-br from-fuchsia-500 to-pink-600 rounded-lg flex items-center justify-center text-white text-sm">3</span>
                  Descreva o evento raiz (primeira vez que sentiu isso)
                </span>
              </label>
              <textarea
                value={eventoAtualData.evento}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    atualizarEvento('evento', e.target.value);
                  }
                }}
                placeholder="Ex: Aos 10 anos, meu pai me criticou duramente na frente de outras pessoas..."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all resize-none text-gray-900"
                rows={4}
              ></textarea>
              <div className="text-right text-sm text-gray-500 mt-1">
                {eventoAtualData.evento.length}/500 caracteres
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                <span className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-gradient-to-br from-pink-500 to-rose-600 rounded-lg flex items-center justify-center text-white text-sm">4</span>
                  Que decisão limitante você tomou naquele momento?
                </span>
              </label>
              <textarea
                value={eventoAtualData.decisaoLimitante}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    atualizarEvento('decisaoLimitante', e.target.value);
                  }
                }}
                placeholder="Ex: Decidi que não sou bom o suficiente e preciso ser perfeito para ser amado..."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all resize-none text-gray-900"
                rows={3}
              ></textarea>
              <div className="text-right text-sm text-gray-500 mt-1">
                {eventoAtualData.decisaoLimitante.length}/500 caracteres
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                <span className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-gradient-to-br from-rose-500 to-red-600 rounded-lg flex items-center justify-center text-white text-sm">5</span>
                  Qual aprendizado valioso você pode preservar?
                </span>
              </label>
              <textarea
                value={eventoAtualData.aprendizado}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    atualizarEvento('aprendizado', e.target.value);
                  }
                }}
                placeholder="Ex: Aprendi a ser mais empático com pessoas que sofrem críticas..."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all resize-none text-gray-900"
                rows={3}
              ></textarea>
              <div className="text-right text-sm text-gray-500 mt-1">
                {eventoAtualData.aprendizado.length}/500 caracteres
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                <span className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center text-white text-sm">6</span>
                  Novo significado empoderador para o evento
                </span>
              </label>
              <textarea
                value={eventoAtualData.novoSignificado}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    atualizarEvento('novoSignificado', e.target.value);
                  }
                }}
                placeholder="Ex: Aquela experiência me ensinou resiliência. Hoje sei que meu valor não depende da aprovação dos outros..."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all resize-none text-gray-900"
                rows={3}
              ></textarea>
              <div className="text-right text-sm text-gray-500 mt-1">
                {eventoAtualData.novoSignificado.length}/500 caracteres
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                <span className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center text-white text-sm">7</span>
                  Visualize seu futuro motivador (sem essa limitação)
                </span>
              </label>
              <textarea
                value={eventoAtualData.futuroDesejado}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    atualizarEvento('futuroDesejado', e.target.value);
                  }
                }}
                placeholder="Ex: Me vejo confiante, expressando minhas ideias livremente, sendo respeitado e valorizado..."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all resize-none text-gray-900"
                rows={4}
              ></textarea>
              <div className="text-right text-sm text-gray-500 mt-1">
                {eventoAtualData.futuroDesejado.length}/500 caracteres
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                <span className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-lg flex items-center justify-center text-white text-sm">8</span>
                  Intensidade Emocional DEPOIS (0-10)
                </span>
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={eventoAtualData.intensidadeDepois}
                  onChange={(e) => atualizarEvento('intensidadeDepois', parseInt(e.target.value))}
                  className="flex-1 h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, rgb(16, 185, 129) 0%, rgb(16, 185, 129) ${eventoAtualData.intensidadeDepois * 10}%, rgb(229, 231, 235) ${eventoAtualData.intensidadeDepois * 10}%, rgb(229, 231, 235) 100%)`
                  }}
                />
                <span className="text-2xl font-bold text-emerald-600 w-12 text-center">
                  {eventoAtualData.intensidadeDepois}
                </span>
              </div>
              {eventoAtualData.intensidadeAntes > eventoAtualData.intensidadeDepois && (
                <div className="mt-4 bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded-r-xl">
                  <div className="flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-emerald-600 text-xl"></i>
                    <p className="text-sm text-emerald-800">
                      <strong>Ótimo progresso!</strong> Você reduziu a intensidade em {eventoAtualData.intensidadeAntes - eventoAtualData.intensidadeDepois} pontos.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={() => {
            const dataStr = JSON.stringify(eventos, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `timeline-worksheet-${new Date().toISOString().split('T')[0]}.json`;
            link.click();
          }}
          className="fixed bottom-8 right-8 w-14 h-14 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 flex items-center justify-center z-40 cursor-pointer"
          title="Salvar Worksheet"
        >
          <i className="ri-save-line text-2xl"></i>
        </button>
      </div>
    </section>
  );
}
